/**
 * CrewChatPage ✦ — /crew/chat/:threadId
 *
 * Action-oriented chat — supplier ↔ crew. Mobile-first dark mode
 * with neon accents. Quick-reply chips (role-aware) above the
 * composer dramatically reduce friction. The event-context strip
 * at top keeps every conversation tethered to the shift.
 *
 * No reactions. No GIFs. No voice notes. Text + intent only.
 * Rate-limited server-side; blocked threads collapse the composer
 * into a calm 'This conversation has ended ✦' chip.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, PaperPlaneRight, Warning, Prohibit, CalendarBlank,
  MapPin, CurrencyGbp, DotsThreeVertical, Lightning,
} from '@phosphor-icons/react';
import CrewPushOptIn from '../../components/crew/CrewPushOptIn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const SUPPLIER_QUICK_REPLIES = [
  "You're accepted ✦",
  'See you at 6pm',
  'Can you arrive earlier?',
  'Thanks for applying',
  'Great work tonight ✦',
];
const CREW_QUICK_REPLIES = [
  'Sounds good',
  'On my way',
  'Thank you!',
  'I can do that',
  'Running 5 mins late',
];

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return ''; }
};

const POLL_INTERVAL_MS = 6000;

const CrewChatPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'block' | 'report'
  const [actionToast, setActionToast] = useState('');
  const [pushTrigger, setPushTrigger] = useState(false);
  const listRef = useRef(null);
  const lastFetchedAt = useRef('');

  const scrollToEnd = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight + 800;
  };

  const loadInitial = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [t, m] = await Promise.all([
        axios.get(`${API}/crew/threads/${threadId}`),
        axios.get(`${API}/crew/threads/${threadId}/messages`),
      ]);
      setThread(t.data);
      setMessages(m.data?.messages || []);
      lastFetchedAt.current = (m.data?.messages || []).slice(-1)[0]?.created_at || '';
      await axios.post(`${API}/crew/threads/${threadId}/read`).catch(() => {});
      setTimeout(scrollToEnd, 60);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/messages');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load thread');
    } finally {
      setLoading(false);
    }
  }, [threadId, navigate]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Lightweight polling — pause when the tab is hidden so phones
  // on long event nights don't keep waking the radio.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!alive || document.hidden) return;
      try {
        const r = await axios.get(`${API}/crew/threads/${threadId}/messages`, {
          params: lastFetchedAt.current ? { since: lastFetchedAt.current } : {},
        });
        const fresh = r.data?.messages || [];
        if (fresh.length > 0) {
          setMessages((prev) => [...prev, ...fresh]);
          lastFetchedAt.current = fresh.slice(-1)[0]?.created_at || lastFetchedAt.current;
          await axios.post(`${API}/crew/threads/${threadId}/read`).catch(() => {});
          setTimeout(scrollToEnd, 60);
        }
      } catch { /* silent */ }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [threadId]);

  const send = async (override) => {
    const value = (override ?? text).trim();
    if (!value || busy) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/crew/threads/${threadId}/messages`, { text: value });
      setMessages((prev) => [...prev, r.data?.message]);
      lastFetchedAt.current = r.data?.message?.created_at || lastFetchedAt.current;
      setText('');
      setPushTrigger(true);
      setTimeout(scrollToEnd, 30);
    } catch (err) {
      if (err?.response?.status === 429) {
        setError('Slow down a bit — too many messages');
      } else if (err?.response?.status === 403) {
        setError('This conversation has ended');
        // Refresh thread so the composer collapses correctly
        try {
          const t = await axios.get(`${API}/crew/threads/${threadId}`);
          setThread(t.data);
        } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not send');
      }
    } finally {
      setBusy(false);
    }
  };

  const doBlock = async () => {
    try {
      await axios.post(`${API}/crew/threads/${threadId}/block`);
      setActionToast('Conversation ended ✦');
      setConfirm(null); setMenuOpen(false);
      loadInitial();
      setTimeout(() => setActionToast(''), 2200);
    } catch (err) {
      setError("Couldn't end the conversation");
    }
  };

  const doReport = async () => {
    try {
      await axios.post(`${API}/crew/threads/${threadId}/report`, { reason: 'flagged from chat' });
      setActionToast('Thanks — we’ll review this thread.');
      setConfirm(null); setMenuOpen(false);
      setTimeout(() => setActionToast(''), 2400);
    } catch (err) {
      setError("Couldn't send report");
    }
  };

  const isSupplier = thread?.viewer_role === 'supplier';
  const quickReplies = isSupplier ? SUPPLIER_QUICK_REPLIES : CREW_QUICK_REPLIES;
  const isBlocked = thread?.status === 'blocked';

  return (
    <div data-testid="crew-chat-page" style={{
      minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 14% 12%, ${PURPLE}1F 0%, transparent 42%), radial-gradient(circle at 86% 88%, ${PINK}14 0%, transparent 42%)`,
      }} />

      {/* ── Header + event context ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(7,7,26,0.85)', backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${PANEL_BORDER}`,
        padding: '14px clamp(14px, 4vw, 24px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate('/crew/messages')}
            data-testid="chat-back"
            style={{
              background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13,
            }}
          >
            <ArrowLeft size={14} weight="bold" />
          </button>
          {thread && (
            <>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: thread.other_party_picture
                  ? `url(${thread.other_party_picture}) center/cover`
                  : `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 13,
              }}>
                {!thread.other_party_picture && (thread.other_party_name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p data-testid="chat-other-name" style={{
                  margin: 0, fontWeight: 800, fontSize: 14.5,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {thread.other_party_name || (isSupplier ? 'Crew member' : 'Supplier')}
                </p>
                <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 11.5 }}>
                  {isBlocked ? 'Conversation ended ✦' : (isSupplier ? 'Crew member' : 'Supplier')}
                </p>
              </div>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  data-testid="chat-menu"
                  style={{
                    background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
                    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label="Thread menu"
                >
                  <DotsThreeVertical size={20} weight="bold" />
                </button>
                {menuOpen && (
                  <div
                    data-testid="chat-menu-dropdown"
                    style={{
                      position: 'absolute', right: 0, top: 36,
                      background: 'rgba(15,15,32,0.96)', border: `1px solid ${PANEL_BORDER}`,
                      borderRadius: 12, minWidth: 180, padding: 6,
                      boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
                    }}
                  >
                    <button
                      onClick={() => { setConfirm('report'); setMenuOpen(false); }}
                      data-testid="chat-menu-report"
                      style={{
                        width: '100%', padding: '9px 12px', borderRadius: 8,
                        background: 'transparent', border: 'none', color: TEXT,
                        cursor: 'pointer', fontFamily: FONT_STACK, fontSize: 13,
                        display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                      }}
                    >
                      <Warning size={14} weight="bold" color={PINK} /> Report conversation
                    </button>
                    {!isBlocked && (
                      <button
                        onClick={() => { setConfirm('block'); setMenuOpen(false); }}
                        data-testid="chat-menu-block"
                        style={{
                          width: '100%', padding: '9px 12px', borderRadius: 8,
                          background: 'transparent', border: 'none', color: TEXT,
                          cursor: 'pointer', fontFamily: FONT_STACK, fontSize: 13,
                          display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                        }}
                      >
                        <Prohibit size={14} weight="bold" color={PINK} /> End conversation
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Event context strip */}
        {thread?.opportunity && (
          <div
            data-testid="chat-context"
            style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 12,
              background: 'rgba(108,59,255,0.10)',
              border: `1px solid ${PURPLE}33`,
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
              fontSize: 12,
            }}
          >
            <Lightning size={13} weight="fill" color={PINK} />
            <span style={{ fontWeight: 800, color: TEXT }}>{thread.opportunity.title}</span>
            <span style={{ color: TEXT_MUTED }}>·</span>
            <span style={{ color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <CalendarBlank size={11} weight="bold" /> {fmtDate(thread.opportunity.event_date)}
              {thread.opportunity.start_time ? ` · ${thread.opportunity.start_time}` : ''}
            </span>
            <span style={{ color: TEXT_MUTED }}>·</span>
            <span style={{ color: TEXT_MUTED, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <MapPin size={11} weight="bold" /> {thread.opportunity.location}
            </span>
            {thread.opportunity.pay && (
              <>
                <span style={{ color: TEXT_MUTED }}>·</span>
                <span style={{ color: BLUE, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <CurrencyGbp size={11} weight="bold" /> {thread.opportunity.pay}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Messages list ── */}
      <div
        ref={listRef}
        data-testid="chat-list"
        style={{
          flex: 1, overflowY: 'auto',
          padding: '18px clamp(14px, 4vw, 24px) 12px',
          display: 'flex', flexDirection: 'column', gap: 8,
          position: 'relative', zIndex: 1,
        }}
      >
        {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
        {!loading && messages.length === 0 && (
          <p data-testid="chat-empty" style={{ color: TEXT_MUTED, fontSize: 13.5, textAlign: 'center', marginTop: 30, lineHeight: 1.55 }}>
            No messages yet — drop a quick hello below ✦
          </p>
        )}
        {messages.map((m) => {
          const mine = m.sender_role === thread?.viewer_role;
          return (
            <div
              key={m.id}
              data-testid={`chat-msg-${m.id}`}
              data-role={mine ? 'me' : 'them'}
              style={{
                alignSelf: mine ? 'flex-end' : 'flex-start',
                maxWidth: '78%', padding: '10px 14px', borderRadius: 18,
                background: mine
                  ? `linear-gradient(135deg, ${PURPLE}, ${PINK})`
                  : 'rgba(255,255,255,0.06)',
                border: mine ? 'none' : `1px solid ${PANEL_BORDER}`,
                color: TEXT, fontSize: 14, lineHeight: 1.45,
                boxShadow: mine ? `0 6px 16px ${PURPLE}44` : 'none',
              }}
            >
              <p style={{ margin: 0, wordBreak: 'break-word' }}>{m.text}</p>
              <p style={{
                margin: '4px 0 0', fontSize: 10, opacity: 0.7, textAlign: mine ? 'right' : 'left',
              }}>
                {fmtTime(m.created_at)}
              </p>
            </div>
          );
        })}
      </div>

      {/* ── Composer or blocked chip ── */}
      <div style={{
        borderTop: `1px solid ${PANEL_BORDER}`,
        background: 'rgba(7,7,26,0.92)',
        backdropFilter: 'blur(12px)',
        padding: '12px clamp(12px, 3vw, 22px)',
        position: 'sticky', bottom: 0, zIndex: 10,
      }}>
        {isBlocked ? (
          <p data-testid="chat-blocked-banner" style={{
            margin: 0, padding: '10px 14px', borderRadius: 999,
            background: 'rgba(255,255,255,0.06)', border: `1px solid ${PANEL_BORDER}`,
            textAlign: 'center', color: TEXT_MUTED, fontSize: 13, fontWeight: 700,
          }}>
            This conversation has ended ✦
          </p>
        ) : (
          <>
            {error && (
              <p data-testid="chat-error" style={{
                margin: '0 0 8px', color: PINK, fontSize: 12.5,
              }}>{error}</p>
            )}

            {/* Quick reply chips */}
            <div
              data-testid="chat-quick-replies"
              style={{
                display: 'flex', gap: 6, overflowX: 'auto',
                paddingBottom: 8, marginBottom: 8,
                scrollbarWidth: 'none',
              }}
            >
              {quickReplies.map((q, i) => (
                <button
                  key={i}
                  onClick={() => send(q)}
                  disabled={busy}
                  data-testid={`chat-quick-${i}`}
                  style={{
                    padding: '7px 12px', borderRadius: 999,
                    background: `linear-gradient(135deg, ${PURPLE}22, ${PINK}22)`,
                    border: `1px solid ${PINK}55`, color: TEXT,
                    cursor: busy ? 'wait' : 'pointer',
                    fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="chat-input"
                placeholder="Type a message…"
                maxLength={600}
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${PANEL_BORDER}`,
                  color: TEXT, fontFamily: FONT_STACK, fontSize: 14, outline: 'none',
                }}
              />
              <button
                onClick={() => send()}
                disabled={busy || !text.trim()}
                data-testid="chat-send"
                style={{
                  padding: '0 16px', borderRadius: 999, border: 'none',
                  cursor: (busy || !text.trim()) ? 'wait' : 'pointer',
                  color: TEXT, fontFamily: FONT_STACK, fontWeight: 900,
                  background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                  boxShadow: `0 6px 18px ${PURPLE}44`,
                  opacity: (busy || !text.trim()) ? 0.55 : 1,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div
          data-testid={`chat-confirm-${confirm}`}
          onClick={() => setConfirm(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 30,
            background: 'rgba(7,7,26,0.78)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 18,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            maxWidth: 360, width: '100%',
            background: 'rgba(15,15,32,0.96)', borderRadius: 18,
            border: `1px solid ${PANEL_BORDER}`,
            padding: 22,
          }}>
            <p style={{ margin: '0 0 8px', fontWeight: 900, fontSize: 18 }}>
              {confirm === 'block' ? 'End this conversation?' : 'Report this conversation?'}
            </p>
            <p style={{ margin: '0 0 16px', color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.5 }}>
              {confirm === 'block'
                ? "Neither side will be able to send more messages. Past messages are preserved."
                : "We'll review the conversation. Thanks for flagging it."}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setConfirm(null)}
                data-testid="chat-confirm-cancel"
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 999,
                  background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
                  color: TEXT_MUTED, cursor: 'pointer', fontFamily: FONT_STACK,
                  fontSize: 13.5, fontWeight: 700,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirm === 'block' ? doBlock : doReport}
                data-testid="chat-confirm-yes"
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 999, border: 'none',
                  background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                  color: TEXT, cursor: 'pointer', fontFamily: FONT_STACK,
                  fontSize: 13.5, fontWeight: 800,
                  boxShadow: `0 6px 18px ${PURPLE}55`,
                }}
              >
                {confirm === 'block' ? 'End ✦' : 'Report ✦'}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionToast && (
        <div data-testid="chat-toast" style={{
          position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 18px', borderRadius: 999,
          background: 'rgba(15,15,32,0.94)', border: `1px solid ${BLUE}66`,
          color: TEXT, fontSize: 13, fontWeight: 700, zIndex: 50,
          boxShadow: `0 8px 24px ${BLUE}33`,
        }}>
          {actionToast}
        </div>
      )}

      <CrewPushOptIn trigger={pushTrigger} source="chat" />
    </div>
  );
};

export default CrewChatPage;
